import React, { useCallback } from 'react';
import { ButtonItem } from '@atlaskit/menu';
import JiraFullscreenButton from '@atlassian/jira-fullscreen-button';
import { useIntl } from '@atlassian/jira-intl';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { ShortcutLabel } from '@atlassian/jira-shortcut-label';
import {
	useViewSettingsActions,
	useIsFullscreen as useIsFullscreenNav3,
} from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import messages from './messages';

interface Props {
	boardId: number;
}

const getFullscreenRefs = (): (HTMLElement | null)[] =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, jira/jira-ssr/no-unchecked-globals-usage
	Array.from(document.querySelectorAll('[data-fullscreen-id]')) as (HTMLElement | null)[];

export const FullscreenButton = ({ boardId }: Props) => {
	const { setIsFullscreen } = useViewSettingsActions();
	const isFullscreen = useIsFullscreenNav3();

	const handleFullscreenToggle = useCallback(
		(wasFullscreen: boolean) => {
			setIsFullscreen({ boardId, isFullscreen: !wasFullscreen });
		},
		[boardId, setIsFullscreen],
	);

	return (
		<JiraFullscreenButton
			testId="software-backlog.header.actions-bar.fullscreen-button"
			getCustomRefs={getFullscreenRefs}
			withContainerStyles={false}
			onToggle={handleFullscreenToggle}
			isFullscreen={isFullscreen}
			renderAsResponsiveButton={getWillShowNav4()}
		/>
	);
};

export const FullscreenButtonItem = () => {
	const { formatMessage } = useIntl();
	const [isFullscreen, { setIsFullscreen }] = useIsFullscreen();

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen(!isFullscreen);
	}, [isFullscreen, setIsFullscreen]);

	return (
		<ButtonItem
			iconAfter={<ShortcutLabel text="Z" />}
			onClick={toggleFullscreen}
			aria-keyshortcuts="Z"
		>
			{isFullscreen
				? formatMessage(messages.exitFullScreen)
				: formatMessage(messages.enterFullScreen)}
		</ButtonItem>
	);
};
